// Shared 360-profile component used by Campus 360 / Staff 360 / Student 360.
// All charts use mocked data; profile pic + banner are stored in localStorage.
import { useState } from "react";
import { useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Camera, ImageIcon, Check, Ban, TrendingUp, Search } from "lucide-react";

const readFile = (file) => new Promise((resolve) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.readAsDataURL(file);
});

export function Profile360({ kind, title, items, getMeta, getCharts }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const focusId = searchParams.get("id");
  // Derive selection straight from URL — no state/effect dance required.
  const selectedId = (focusId && items.some((i) => i.id === focusId)) ? focusId : (items[0]?.id || "");
  const item = items.find((i) => i.id === selectedId);

  if (!item) {
    return <AppShell><div className="p-8 text-sm text-muted-foreground">No {kind} available.</div></AppShell>;
  }

  const meta = getMeta(item);
  const { attendance, performance, academicFees, transportFees } = getCharts(item);

  // Filter items list by search.
  const filteredItems = search.trim()
    ? items.filter((i) => i.label.toLowerCase().includes(search.toLowerCase()))
    : items;

  // Title gets a degree symbol where the source title says "360".
  const displayTitle = title.replace(/360/g, "360°");

  return (
    <AppShell>
      <div className="px-6 py-5" data-testid={`${kind}-360-page`}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="font-display font-bold tracking-tight text-2xl">{displayTitle}</h1>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={`Search ${kind}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-8 w-[220px]"
                data-testid={`${kind}-360-search`}
              />
            </div>
            <div className="min-w-[260px]">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">{kind}</Label>
              <Select value={selectedId} onValueChange={(v) => setSearchParams({ id: v })}>
                <SelectTrigger data-testid={`${kind}-360-picker`}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {filteredItems.map((i) => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}
                  {filteredItems.length === 0 && (
                    <div className="px-2 py-3 text-xs text-muted-foreground text-center">No matches</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <ProfileHeader key={item.id} kind={kind} item={item} meta={meta} attendance={attendance} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <Card title="Performance graph"><BarLineChart data={performance} /></Card>
          <Card title="Attendance details"><DonutChart present={attendance.percent} /></Card>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="Academic fees"><FeeDonut paid={academicFees.paid} due={academicFees.due} pending={academicFees.pending} status={academicFees.status} /></Card>
          <Card title="Transport fees"><FeeDonut paid={transportFees.paid} due={transportFees.due} pending={transportFees.pending} status={transportFees.status} /></Card>
        </div>
      </div>
    </AppShell>
  );
}

function ProfileHeader({ kind, item, meta, attendance }) {
  const storageKey = (suffix) => `kampus365-360-${kind}-${item.id}-${suffix}`;
  const [photo, setPhoto] = useState(() => localStorage.getItem(storageKey("photo")) || "");
  const [banner, setBanner] = useState(() => localStorage.getItem(storageKey("banner")) || "");
  const photoRef = useRef(null);
  const bannerRef = useRef(null);
  const onPhoto = async (f) => { const d = await readFile(f); localStorage.setItem(storageKey("photo"), d); setPhoto(d); };
  const onBanner = async (f) => { const d = await readFile(f); localStorage.setItem(storageKey("banner"), d); setBanner(d); };

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden mb-4">
      <div
        className="h-32 bg-gradient-to-br from-primary/30 via-primary/15 to-secondary relative cursor-pointer group"
        style={banner ? { backgroundImage: `url(${banner})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        onClick={() => bannerRef.current?.click()}
        data-testid={`${kind}-360-banner`}
      >
        <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onBanner(e.target.files[0])} />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="bg-card/90 px-2 py-1 rounded text-xs flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Click to change banner</div>
        </div>
      </div>
      <div className="px-5 pb-5 -mt-12 flex items-end gap-4 flex-wrap">
        <div className="relative shrink-0 group cursor-pointer" onClick={() => photoRef.current?.click()} data-testid={`${kind}-360-photo`}>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onPhoto(e.target.files[0])} />
          <div
            className="h-24 w-24 rounded-full border-4 border-card bg-secondary flex items-center justify-center font-display font-bold text-2xl text-primary overflow-hidden"
            style={photo ? { backgroundImage: `url(${photo})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
          >
            {!photo && meta.initials}
          </div>
          <div className="absolute bottom-0 right-0 h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
            <Camera className="h-3.5 w-3.5" />
          </div>
        </div>
        <div className="flex-1 min-w-[260px] pt-12">
          <div className="font-display font-bold text-xl tracking-tight">{meta.title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{meta.subtitle}</div>
        </div>
        <div className="flex gap-2 flex-wrap pt-12">
          {meta.fields.map((f) => (
            <div key={f.label} className="border border-border rounded-md px-3 py-1.5 bg-muted/30">
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{f.label}</div>
              <div className="text-[13px] font-medium">{f.value}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="px-5 pb-5 grid grid-cols-3 gap-3">
        <StatChip color="emerald" icon={<Check className="h-3.5 w-3.5" />} label="Present" value={`${attendance.presentDays} Days`} />
        <StatChip color="rose" icon={<Ban className="h-3.5 w-3.5" />} label="Absent" value={`${attendance.absentDays} Days`} />
        <StatChip color="amber" icon={<TrendingUp className="h-3.5 w-3.5" />} label="Attendance" value={`${attendance.percent}%`} />
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-border rounded-lg bg-card p-5">
      <div className="font-display font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

function StatChip({ color, icon, label, value }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700 hover:shadow-emerald-200/50",
    rose: "bg-rose-50 border-rose-200 text-rose-700 hover:shadow-rose-200/50",
    amber: "bg-amber-50 border-amber-200 text-amber-700 hover:shadow-amber-200/50",
  }[color];
  return (
    <div className={`border rounded-xl px-4 py-2.5 flex items-center gap-3 ${tones} shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.02] transition-all duration-300 cursor-pointer animate-in fade-in slide-in-from-bottom-1`}>
      <div className="h-9 w-9 rounded-full bg-white/80 flex items-center justify-center shadow-inner">{icon}</div>
      <div>
        <div className="font-display font-bold leading-none text-lg">{value}</div>
        <div className="text-[10px] uppercase tracking-wider opacity-80 mt-1">{label}</div>
      </div>
    </div>
  );
}

// Lean SVG bar + line chart — fully mock.
function BarLineChart({ data }) {
  const W = 480, H = 220, P = 30;
  const max = Math.max(100, ...data.map((d) => d.value));
  const bw = (W - P * 2) / data.length - 6;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56">
      {[0, 20, 40, 60, 80, 100].map((y) => {
        const yp = H - P - ((y / max) * (H - P * 2));
        return <line key={y} x1={P} x2={W - P} y1={yp} y2={yp} stroke="hsl(var(--border))" strokeDasharray="2 3" />;
      })}
      {data.map((d, i) => {
        const x = P + i * ((W - P * 2) / data.length) + 3;
        const h = (d.value / max) * (H - P * 2);
        const y = H - P - h;
        return <rect key={i} x={x} y={y} width={bw} height={h} fill="#3b82f6" rx="2" />;
      })}
      <polyline
        points={data.map((d, i) => {
          const x = P + i * ((W - P * 2) / data.length) + 3 + bw / 2;
          const y = H - P - ((d.value / max) * (H - P * 2));
          return `${x},${y}`;
        }).join(" ")}
        fill="none" stroke="#a855f7" strokeWidth="2"
      />
      {data.map((d, i) => {
        const x = P + i * ((W - P * 2) / data.length) + 3 + bw / 2;
        return <text key={i} x={x} y={H - 8} fontSize="9" textAnchor="middle" fill="hsl(var(--muted-foreground))">{d.label}</text>;
      })}
    </svg>
  );
}

function DonutChart({ present }) {
  const leave = 100 - present;
  const C = 2 * Math.PI * 50;
  return (
    <div className="flex items-center gap-5">
      <svg viewBox="0 0 130 130" className="h-44 w-44">
        <circle cx="65" cy="65" r="50" fill="none" stroke="hsl(var(--muted))" strokeWidth="18" />
        <circle cx="65" cy="65" r="50" fill="none" stroke="#22c55e" strokeWidth="18"
                strokeDasharray={`${(present / 100) * C} ${C}`} transform="rotate(-90 65 65)" />
        <circle cx="65" cy="65" r="50" fill="none" stroke="#ef4444" strokeWidth="18"
                strokeDasharray={`${(leave / 100) * C} ${C}`}
                strokeDashoffset={`${-((present / 100) * C)}`} transform="rotate(-90 65 65)" />
        <text x="65" y="68" textAnchor="middle" fontSize="18" fontWeight="700" fill="hsl(var(--foreground))">{present}%</text>
      </svg>
      <div className="space-y-2 text-xs">
        <Legend color="#22c55e" label={`Present - ${present}%`} />
        <Legend color="#ef4444" label={`Leave - ${leave}%`} />
      </div>
    </div>
  );
}

function FeeDonut({ paid, due, pending, status }) {
  const [hover, setHover] = useState(null);
  const total = paid + due + pending || 1;
  const C = 2 * Math.PI * 50;
  const lenPaid = (paid / total) * C;
  const lenPending = (pending / total) * C;
  const lenDue = (due / total) * C;
  const pct = (n) => Math.round((n / total) * 100);
  const compact = (n) => n >= 1e7 ? `${(n/1e7).toFixed(2)} Cr` : n >= 1e5 ? `${(n/1e5).toFixed(2)} L` : n >= 1e3 ? `${(n/1e3).toFixed(1)}k` : `${n}`;
  const slice = (color, len, off, key) => (
    <circle
      key={key}
      cx="65" cy="65" r="50" fill="none" stroke={color}
      strokeWidth={hover === key ? "22" : "18"}
      strokeDasharray={`${len} ${C}`}
      strokeDashoffset={-off}
      transform="rotate(-90 65 65)"
      onMouseEnter={() => setHover(key)}
      onMouseLeave={() => setHover(null)}
      style={{
        filter: hover === key ? "drop-shadow(0 4px 10px rgba(0,0,0,0.25))" : "none",
        transition: "all 250ms ease",
        cursor: "pointer",
      }}
    />
  );
  return (
    <div className="flex items-center gap-5">
      <svg viewBox="0 0 130 130" className="h-40 w-40">
        <circle cx="65" cy="65" r="50" fill="none" stroke="hsl(var(--muted))" strokeWidth="18" />
        {slice("#22c55e", lenPaid, 0, "paid")}
        {slice("#38bdf8", lenPending, lenPaid, "pending")}
        {slice("#ef4444", lenDue, lenPaid + lenPending, "due")}
        <text x="65" y="58" textAnchor="middle" fontSize="8" fill="hsl(var(--muted-foreground))">TOTAL</text>
        <text x="65" y="74" textAnchor="middle" fontSize="13" fontWeight="700" fill="hsl(var(--foreground))">₹{compact(total)}</text>
      </svg>
      <div className="space-y-1.5 text-xs">
        <Legend color="#22c55e" label={`Paid · ₹${compact(paid)} (${pct(paid)}%)`} hot={hover === "paid"} />
        <Legend color="#38bdf8" label={`Pending · ₹${compact(pending)} (${pct(pending)}%)`} hot={hover === "pending"} />
        <Legend color="#ef4444" label={`Due · ₹${compact(due)} (${pct(due)}%)`} hot={hover === "due"} />
        <Badge variant="outline" className={`mt-1 ${status === "paid" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-rose-300 bg-rose-50 text-rose-700"}`} data-testid="fee-status">
          {status === "paid" ? <Check className="h-3 w-3 mr-1" /> : <Ban className="h-3 w-3 mr-1" />}
          {status === "paid" ? "Paid this month" : "Pending this month"}
        </Badge>
      </div>
    </div>
  );
}

function Legend({ color, label, hot }) {
  return (
    <div className={`flex items-center gap-2 transition-all ${hot ? "font-semibold scale-[1.04]" : ""}`}>
      <span className={`rounded-full transition-all ${hot ? "h-3.5 w-3.5" : "h-2.5 w-2.5"}`} style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}
